/**
 * Finding a seat — the card between pressing Play and the first card being dealt.
 *
 * ============================== WHAT IT IS FOR =================================
 * Master plan §6 asks for a 2-5 second card before every sit-down, in every room, and
 * lists what goes on it: the door, the cast sitting down one by one, the stakes in
 * Bucks, the session commitment, and one trainer hook. Every one of those is a thing
 * the product needed a place for anyway, and this is the place:
 *
 *   - THE DELAY IS THE CAST. There is no spinner. The portraits fading in at 400 ms
 *     apart is what the two seconds are made of, so the wait is watching people sit
 *     down at a table rather than watching a wheel.
 *   - THE PRELOAD LIVES HERE NOW. Phase 3b put a 3-second image preload in front of
 *     the first hand precisely so the felt would not open on green discs, and it was
 *     three seconds of nothing. It is the same three seconds, spent on a picture.
 *   - THE COMMITMENT IS ON SCREEN FROM SIT-DOWN, which is what `FAIRNESS.md` says the
 *     product does and what, until this phase, it did not: the only place the session
 *     commitment appeared was `HandHistory`, below the fold of the table screen.
 *   - THE SEAT IS BOUGHT HERE. Not before the card and not after it. The card opens,
 *     the buy-in posts, the cast sits, and the felt appears already funded — so a 402
 *     turns into the can't-afford state WITH the Barkeep in it, and the player never
 *     sees a felt they cannot pay for.
 *
 * ------------------------------ WHAT IS A PROP ---------------------------------
 * The door art, the price, the commitment, the cast, the hook and the scenario brief
 * all arrive as props, and the buy is a callback. This component owns the STATE
 * MACHINE and the clock and nothing else, which is what lets blackjack use it without
 * either room knowing about the other, and what lets the harness freeze it mid-fade.
 * ===============================================================================
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { preloadTable } from "../table/preload";
import { SEAT_COPY, type SeatHook } from "./copy";
import { Barkeep } from "./Barkeep";
import "./rooms.css";

/**
 * The three numbers the card is made of.
 *
 * `MIN` is a floor and not a wait: with every image cached and two seats to fill, the
 * cast is seated in 800 ms and the card would flash. `MAX` is the ceiling the whole
 * thing is bounded by, and it is deliberately just above `MOTION.preloadCeiling` (3 s)
 * so that a slow connection spends its budget on pictures and still deals on time.
 * `STEP` is one seat sitting down.
 */
export const SEAT_MIN_MS = 2000;
export const SEAT_MAX_MS = 5000;
export const SEAT_STEP_MS = 400;

export type SeatPhase = "open" | "buying" | "seating" | "ready" | "go" | "cannot-afford";

/** What a seat costs, or null for a table that costs nothing (a drill-shaped seat). */
export interface SeatPrice { cost: number; line: ReactNode }

export interface SeatFinderProps {
  /** The card's own name — "Quick Play", "Bubble Pressure". */
  title: string;
  /** One line of context drawn from the card: "Bubble: one place off the money." */
  brief?: string | null;
  price?: SeatPrice | null;
  /** The session commitment, published before the first card. */
  commitment: string;
  /** Every seat, in the engine's own order, hero included. */
  cast: readonly { name: string; avatar?: string | null; isSelf?: boolean }[];
  /** §7's one line, or null for a player with no history. */
  hook?: SeatHook | null;
  /** The backdrop. A plate, taken as a prop like every other plate. */
  door?: string;
  /** Cast names for the image preload. Defaults to the cast's own names. */
  preloadNames?: readonly string[];
  /**
   * Buy the seat. Resolves `ok` when the wallet moved (or there was nothing to move),
   * and with the two numbers when it could not be afforded.
   */
  buy: () => Promise<{ ok: true } | { ok: false; cost: number; balance: number }>;
  /** Called once, when the card is done and the table may be dealt. */
  onGo: () => void;
  /** The way out of the can't-afford state that is not the Barkeep. */
  onBack: () => void;
  /** Whether the Barkeep will stake this player — Phase 4's own rule. */
  canStake?: boolean;
  /**
   * The harness's freeze. Given, no clock runs and no seat is bought: the card renders
   * exactly the state named, which is how a mid-fade is reviewed as a still.
   */
  freeze?: { phase: SeatPhase; seated?: number; partial?: number; short?: { cost: number; balance: number } };
}

const DEFAULT_DOOR = "/art/scenes/saloon-door-1000.webp";

export function SeatFinder(props: SeatFinderProps) {
  const {
    title, brief, price, commitment, cast, hook, door = DEFAULT_DOOR,
    preloadNames, buy, onGo, onBack, canStake = false, freeze,
  } = props;

  const [phase, setPhase] = useState<SeatPhase>(freeze?.phase ?? "open");
  const [seated, setSeated] = useState(freeze?.seated ?? 0);
  const [short, setShort] = useState<{ cost: number; balance: number } | null>(freeze?.short ?? null);
  const [barkeep, setBarkeep] = useState(false);
  /** Bumped by a retry, which is what re-runs the whole effect. */
  const [attempt, setAttempt] = useState(0);

  /*
   * Everything the clock reads, off refs.
   *
   * The same trap `Play.tsx` has sprung three times: a callback in a dependency list
   * that is a new closure every render re-runs the effect, and this effect BUYS A SEAT.
   * So the effect depends on the attempt number and nothing else, and reads the rest
   * through refs that are refreshed on every render.
   */
  const live = useRef({ buy, onGo, cast, preloadNames });
  live.current = { buy, onGo, cast, preloadNames };
  const went = useRef(false);

  useEffect(() => {
    if (freeze) return;
    let alive = true;
    const t0 = Date.now();
    let assetsAt: number | null = null;
    let seatedAt: number | null = null;
    let bought = false;
    const n = live.current.cast.length;

    /* Declared before the promises that fill it: a `const` referenced from an async
       callback before its own declaration line is a temporal-dead-zone trap waiting
       for the day the callback resolves synchronously. */
    const timers: number[] = [];

    setPhase("buying");
    setSeated(0);
    went.current = false;

    /* The pictures, in parallel with the money. `preloadTable` never rejects and is
       bounded by its own 3-second ceiling, which is inside this card's 5. */
    const names = live.current.preloadNames ?? live.current.cast.map((c) => c.name);
    void preloadTable(names).then(() => { if (alive) assetsAt = Date.now(); });

    void live.current.buy().then((r) => {
      if (!alive) return;
      if (!r.ok) { setShort({ cost: r.cost, balance: r.balance }); setPhase("cannot-afford"); return; }
      bought = true;
      setShort(null);
      setPhase("seating");
      /* One seat every STEP, starting immediately: the first player is already there. */
      let k = 0;
      const sit = window.setInterval(() => {
        if (!alive) return;
        k += 1;
        setSeated(k);
        if (k >= n) { window.clearInterval(sit); seatedAt = Date.now(); }
      }, SEAT_STEP_MS);
      timers.push(sit);
      if (n === 0) seatedAt = Date.now();
    });

    /*
     * One poll rather than a race of timeouts.
     *
     * The rule is "whichever of assets-loaded and cast-seated comes last, then go",
     * floored at MIN and capped at MAX — which is a condition over three clocks and one
     * promise, and expressing that as nested timeouts is how a card ends up dealing
     * twice or never. A hundred milliseconds is finer than the eye and cheaper than a
     * single re-render.
     */
    const tick = window.setInterval(() => {
      if (!alive || went.current || !bought) return;
      const elapsed = Date.now() - t0;
      const ready = assetsAt !== null && seatedAt !== null;
      if (elapsed >= SEAT_MAX_MS || (ready && elapsed >= SEAT_MIN_MS)) {
        went.current = true;
        window.clearInterval(tick);
        setPhase("go");
        live.current.onGo();
      }
    }, 100);
    timers.push(tick);

    return () => { alive = false; for (const t of timers) window.clearInterval(t); };
  }, [attempt, freeze]);

  const retry = useCallback(() => { setBarkeep(false); setAttempt((a) => a + 1); }, []);

  const heading = phase === "cannot-afford" ? SEAT_COPY.cantAfford
    : phase === "buying" ? SEAT_COPY.buying
    : phase === "seating" ? SEAT_COPY.seating
    : SEAT_COPY.finding;

  return (
    <section className="seatcard" data-phase={phase}>
      {/* The door. A plate, exactly as the saloon takes one, and replaceable by a prop. */}
      <div className="seatcard-plate" style={{ backgroundImage: `url(${door})` }} aria-hidden="true" />
      <div className="seatcard-vignette" aria-hidden="true" />

      <div className="seatcard-body">
        {/*
          * The live region is the KICKER, not the card.
          *
          * It was on the whole `<section>`, which contains the title, the price, a
          * sixty-four character hash and up to nine seats that arrive four hundred
          * milliseconds apart — so a screen reader re-announced the entire card nine
          * times while the cast sat down. One line changes, and it is this one, so it
          * is the only thing that speaks.
          */}
        <p className="seatcard-kicker" aria-live="polite">{heading}</p>
        <h1 className="seatcard-title">{title}</h1>
        {brief && <p className="seatcard-brief">{brief}</p>}

        {/* No coin of its own: the price line is built from `BucksAmount`, which draws
            one beside every figure, and a third coin on a pill with two numbers on it
            reads as decoration rather than as a mark. */}
        {price && <p className="seatcard-price">{price.line}</p>}

        {phase === "cannot-afford" && short ? (
          <div className="seatcard-short">
            <p>
              This seat costs <b className="num">{short.cost.toLocaleString("en-US")}</b> Bucks.
              You have <b className="num">{short.balance.toLocaleString("en-US")}</b>.
            </p>
            <div className="seatcard-buttons">
              {canStake
                ? <button className="btn" type="button" onClick={() => setBarkeep(true)}>{SEAT_COPY.askBarkeep}</button>
                : <p className="muted">{SEAT_COPY.pickSmaller}</p>}
              <button className="btn-quiet" type="button" onClick={onBack}>{SEAT_COPY.leave}</button>
            </div>
            {/* Phase 4's dialog, unchanged. Taking the stake retries the same seat. */}
            {barkeep && <Barkeep onClose={retry} />}
          </div>
        ) : (
          <ul className="seatcard-cast">
            {cast.map((c, i) => {
              const on = i < seated;
              const going = !on && freeze?.partial !== undefined && i === seated;
              return (
                <li key={`${c.name}-${i}`}
                    /* The cast is decoration for a screen reader: the kicker above says
                       what is happening, and nine names read out one at a time as they
                       fade in is noise on top of it. */
                    aria-hidden={!on || undefined}
                    className={"seatcard-seat" + (on ? " is-seated" : "") + (c.isSelf ? " is-you" : "")}
                    style={going ? { opacity: freeze!.partial, transform: "translateY(4px)" } : undefined}>
                  {c.avatar
                    ? <img src={c.avatar} alt="" aria-hidden="true" />
                    : <span className="seatcard-blank" aria-hidden="true" />}
                  <b>{c.name}</b>
                </li>
              );
            })}
          </ul>
        )}

        {/* The fairness page's requirement: on screen from sit-down, with the sentence
            that says what it is for and what it is not. */}
        <div className="seatcard-fair">
          <span className="seatcard-fair-label">{SEAT_COPY.commitmentLabel}</span>
          <code className="seedcode">{commitment}</code>
          <p className="muted small">{SEAT_COPY.commitmentLine}</p>
        </div>

        {/* §7: one line, one tap, about this player, and nothing at all for a player
            with no history. It is a plain anchor rather than a router Link because the
            card is mounted inside the table route in both rooms. */}
        {hook && phase !== "cannot-afford" && (
          <p className="seatcard-hook">
            {hook.text} <a href={hook.href}>{hook.title}</a>
          </p>
        )}
      </div>
    </section>
  );
}
